"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ServiceDegradedBanner } from "@/components/service-degraded-banner"

const navLinks = [
  { id: "about", label: "about", href: "/about" },
  { id: "services", label: "services", href: "/services" },
  { id: "templates", label: "templates", href: "/templates" },
  { id: "portal", label: "portal", href: "/portal" },
  { id: "docs", label: "docs", href: "/docs" },
  { id: "contact", label: "contact", href: "/contact" },
]

const serviceActions: Record<string, { label: string; service: string; source: string; emailHref: string }> = {
  "/proxmox-health-check": {
    label: "Proxmox Health Check",
    service: "proxmox-health-check",
    source: "proxmox-health-check",
    emailHref: "mailto:support@hsve.co.uk?subject=Proxmox%20Infrastructure%20Health%20Check",
  },
  "/proxmox-support": {
    label: "Managed Proxmox Support",
    service: "managed-proxmox-support",
    source: "proxmox-support",
    emailHref: "mailto:support@hsve.co.uk?subject=Managed%20Proxmox%20Support",
  },
}

export function ResponsiveHeader() {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeMenuButtonRef = useRef<HTMLButtonElement>(null)
  const serviceAction = pathname ? serviceActions[pathname] : undefined

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!mobileMenuOpen) return
    closeMenuButtonRef.current?.focus()
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [mobileMenuOpen])

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    if (href.startsWith("http")) return false
    return pathname?.startsWith(href)
  }

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "var(--bg)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <ServiceDegradedBanner />
        <div
          className="header-padding"
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            padding: "0 48px",
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src="/images/hsve-logo.png"
              alt="HSVE"
              style={{ height: 36, width: "auto" }}
            />
          </Link>

          <nav
            aria-label="Primary navigation"
            className="font-mono hide-mobile"
            style={{
              display: "flex",
              gap: 22,
              fontSize: 12,
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                aria-current={isActive(link.href) ? "page" : undefined}
                style={{
                  color: isActive(link.href) ? "var(--accent)" : "var(--text-2)",
                  textDecoration: "none",
                }}
              >
                <span style={{ color: "var(--muted)" }}>~/</span>
                {link.label}
              </Link>
            ))}
          </nav>

          <div
            className="font-mono hide-mobile"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              fontSize: 12,
            }}
          >
            <a
              href="https://status.hsve.cc"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--text-2)",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span className="status-dot" />
              status
            </a>
            <Link href="/login" style={{ color: "var(--text-2)", textDecoration: "none" }}>
              account()
            </Link>
            <a
              href="https://portal.hsve.co.uk"
              style={{
                color: "var(--accent-ink)",
                background: "var(--accent)",
                border: "1px solid var(--accent)",
                padding: "7px 13px",
                borderRadius: 4,
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              open_portal()
            </a>
          </div>

          <button
            ref={menuButtonRef}
            className={`hamburger-btn hide-desktop ${mobileMenuOpen ? "open" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {serviceAction && (
        <>
          <style>{`main a[href="${serviceAction.emailHref}"] { display: none !important; }`}</style>
          <div style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-soft)" }}>
            <div
              className="header-padding font-mono"
              style={{
                maxWidth: 1240,
                margin: "0 auto",
                padding: "9px 48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 11, color: "var(--text-2)" }}>
                <span style={{ color: "var(--accent)" }}>$</span> enquire --service={serviceAction.service}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Link
                  href={`/contact?service=${serviceAction.service}&source=${serviceAction.source}`}
                  style={{
                    background: "var(--accent)",
                    color: "var(--accent-ink)",
                    padding: "7px 11px",
                    borderRadius: 4,
                    textDecoration: "none",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Start {serviceAction.label} enquiry
                </Link>
                <a href={serviceAction.emailHref} style={{ color: "var(--muted)", textDecoration: "none", fontSize: 10 }}>
                  email instead
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className={`mobile-nav-overlay ${mobileMenuOpen ? "open" : ""}`}
        aria-hidden="true"
        onClick={() => {
          setMobileMenuOpen(false)
          menuButtonRef.current?.focus()
        }}
      />

      <div
        id="mobile-navigation"
        className={`mobile-nav-drawer ${mobileMenuOpen ? "open" : ""}`}
        role="dialog"
        aria-modal={mobileMenuOpen ? "true" : undefined}
        aria-label="Mobile navigation"
        aria-hidden={!mobileMenuOpen}
        inert={!mobileMenuOpen}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
              <img src="/images/hsve-logo.png" alt="HSVE" style={{ height: 32, width: "auto" }} />
            </Link>
            <button
              ref={closeMenuButtonRef}
              className="hamburger-btn open"
              onClick={() => {
                setMobileMenuOpen(false)
                menuButtonRef.current?.focus()
              }}
              aria-label="Close menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        <nav
          aria-label="Mobile navigation links"
          className="font-mono"
          style={{
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              aria-current={isActive(link.href) ? "page" : undefined}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                color: isActive(link.href) ? "var(--accent)" : "var(--text-2)",
                textDecoration: "none",
                padding: "14px 0",
                borderBottom: "1px solid var(--border-soft)",
                fontSize: 14,
              }}
            >
              <span style={{ color: "var(--muted)" }}>~/</span>
              {link.label}
            </Link>
          ))}
        </nav>

        <div
          className="font-mono"
          style={{
            padding: "8px 24px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            fontSize: 13,
          }}
        >
          <a
            href="https://status.hsve.cc"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "var(--text-2)",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 0",
            }}
          >
            <span className="status-dot" />
            status
          </a>
          <Link
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            style={{ color: "var(--text-2)", textDecoration: "none", padding: "10px 0" }}
          >
            account()
          </Link>
          <a
            href="https://portal.hsve.co.uk"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              color: "var(--accent-ink)",
              background: "var(--accent)",
              border: "1px solid var(--accent)",
              padding: "11px 15px",
              borderRadius: 4,
              textDecoration: "none",
              fontWeight: 600,
              textAlign: "center",
              marginTop: 8,
            }}
          >
            open_portal()
          </a>
        </div>
      </div>
    </>
  )
}
